<template>
  <div class="res-defaults"> 
    <div class="header-section">
      <div class="header-left">
        <div class="main-heading">
          Badge Defaults
        </div>
        <div class="main-description mb-5">
          These defaults will be applied when creating badges for this responsibility
        </div>
      </div>
      <div v-if="hasChanges" class="header-right">
        <Button
          size="small"
          label="Discard"
          outline="outline-hover"
          @onClick="handleDiscard"
          class="ex-discard-default"
        />
        <Button
          size="small"
          label="Save"
          :normal="'normal-hover'"
          primary
          @onClick="handleSave"
          class="ex-save-default"
        />
      </div>
    </div>
    <div class="hr mb-6"> </div>
    <v-row>
      <v-col md="4">
        <div class="section-heading">When Given</div>
        <div class="section-description">Given means Inspection was skipped</div>
      </v-col>
      <v-col md="8">
        <v-radio-group v-model="whenGivenSelected" @change="handleWhenGivenChange" column hide-details>
          <v-radio
            :label="whenGivenOptions[0].label"
            :value="whenGivenOptions[0].value"
            color="#7F56D9"
            class="filter-radio-tab"
          ></v-radio>
          <ExTextInput type="number" v-model="whenGivenExpiresInDays" class="days-input" />
          <v-radio
            v-for="(radio, index) in remainingWhenGivenOptions"
            :key="index"
            :label="radio.label"
            :value="radio.value"
            color="#7F56D9"
            class="filter-radio-tab"
          ></v-radio>
        </v-radio-group>
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <div class="hr"> </div>
      </v-col>
    </v-row>
    <v-row>
      <v-col md="4">
        <div class="section-heading">When Awarded</div>
        <div class="section-description">Awarded means Inspection was passed</div>
      </v-col>
      <v-col md="8">
        <v-radio-group v-model="whenAwardedSelected" @change="handleWhenAwardedChange" column hide-details>
          <v-radio
            :label="whenAwardedOptions[0].label"
            :value="whenAwardedOptions[0].value"
            color="#7F56D9"
            class="filter-radio-tab"
          ></v-radio>
          <ExTextInput type="number" v-model="whenAwardedExpiresInDays" class="days-input" />

          <v-radio
            :label="whenAwardedOptions[1].label"
            :value="whenAwardedOptions[1].value"
            color="#7F56D9"
            class="filter-radio-tab"
          ></v-radio>
          <ExTextInput type="number" v-model="whenAwardedExpiresFromObsoleteInDays" class="days-input" />
          
          <v-radio
            :label="whenAwardedOptions[2].label"
            :value="whenAwardedOptions[2].value"
            color="#7F56D9"
            class="filter-radio-tab"
          ></v-radio>
        </v-radio-group>
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <div class="hr"> </div>
      </v-col>  
    </v-row>
  </div>
</template>

<script setup>
import { computed, ref, watch, getCurrentInstance, onMounted } from 'vue';
import { useRoute } from 'vue-router/composables';
import ExTextInput from "@components/TextInput/ExTextInput.vue";
import Button from "@components/buttons/Button.vue";
import { updateResponsibilityDefaults, getResponsibilityIdV2 } from "@/api/responsibilityAPI";

// Get instance for accessing $toast and $loader
const instance = getCurrentInstance();
const proxy = instance?.proxy;

// Get route params
const route = useRoute();

// Define emits
const emit = defineEmits(['input-force', 'input-awarded', 'input-Obsolete', 'createDefaultBadge']);

// Reactive data - using ref for primitive values and reactive for objects
const whenGivenOptions = ref([
  { label: 'Expires in days from issuance', value: 'expire_in_days' },
  { label: 'Does not Expire', value: 'does_not_expire' }
]);

const whenAwardedOptions = ref([
  { label: 'Expires in days from issuance', value: 'expire_in_days' },
  { label: 'Expires in days from becoming Obsolete', value: 'expire_from_obsolete' },
  { label: 'Does not Expire', value: 'does_not_expire' }
]);

const whenGivenSelected = ref('expire_in_days');
const whenAwardedSelected = ref('expire_from_obsolete');

// Current form values
const whenGivenExpiresInDays = ref('365');
const whenAwardedExpiresInDays = ref('365');
const whenAwardedExpiresFromObsoleteInDays = ref('30');

// Original values to track changes
const originalWhenGivenSelected = ref('expire_in_days');
const originalWhenAwardedSelected = ref('expire_from_obsolete');
const originalWhenGivenExpiresInDays = ref('365');
const originalWhenAwardedExpiresInDays = ref('365');
const originalWhenAwardedExpiresFromObsoleteInDays = ref('30');

const remainingWhenGivenOptions = computed(() => {
  return whenGivenOptions.value.slice(1);
});

const hasChanges = computed(() => {
  return (
    whenGivenSelected.value !== originalWhenGivenSelected.value ||
    whenAwardedSelected.value !== originalWhenAwardedSelected.value ||
    whenGivenExpiresInDays.value !== originalWhenGivenExpiresInDays.value ||
    whenAwardedExpiresInDays.value !== originalWhenAwardedExpiresInDays.value ||
    whenAwardedExpiresFromObsoleteInDays.value !== originalWhenAwardedExpiresFromObsoleteInDays.value
  );
});

// Methods
const handleWhenGivenChange = (value) => {
  whenGivenSelected.value = value;
};

const handleWhenAwardedChange = (value) => {
  whenAwardedSelected.value = value;
};

const handleSave = async () => {
  const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
  const data = {
    when_forced_expiration_days_default: parseInt(whenGivenExpiresInDays.value) || 365,
    when_forced_does_not_expire_default: whenGivenSelected.value === 'does_not_expire',
    when_awarded_expiration_days_default: parseInt(whenAwardedExpiresInDays.value) || 365,
    when_awarded_expire_in_days_from_becoming_obsolete: parseInt(whenAwardedExpiresFromObsoleteInDays.value) || 30,
    expire_only_if_obsolete_default: whenAwardedSelected.value === 'expire_from_obsolete',
    when_awarded_does_not_expire_default: whenAwardedSelected.value === 'does_not_expire'
  };

  try {
    proxy.$loader.show();
    const response = await updateResponsibilityDefaults(accountId, route.params.objId, data);
    if (response.status === 200) {
      // Update original values after successful save
      originalWhenGivenSelected.value = whenGivenSelected.value;
      originalWhenAwardedSelected.value = whenAwardedSelected.value;
      originalWhenGivenExpiresInDays.value = whenGivenExpiresInDays.value;
      originalWhenAwardedExpiresInDays.value = whenAwardedExpiresInDays.value;
      originalWhenAwardedExpiresFromObsoleteInDays.value = whenAwardedExpiresFromObsoleteInDays.value;
    }
  } catch (error) {
    console.error('Error saving defaults:', error);
  } finally {
    proxy.$loader.hide();
  }
};

const handleDiscard = () => {
  // Revert to original values
  whenGivenSelected.value = originalWhenGivenSelected.value;
  whenAwardedSelected.value = originalWhenAwardedSelected.value;
  whenGivenExpiresInDays.value = originalWhenGivenExpiresInDays.value;
  whenAwardedExpiresInDays.value = originalWhenAwardedExpiresInDays.value;
  whenAwardedExpiresFromObsoleteInDays.value = originalWhenAwardedExpiresFromObsoleteInDays.value;
};

// Watch for route params changes
watch(() => route.params.objId, async (newId) => {
  if (newId) {
    await fetchResponsibilityDefaults();
  }
});

// Fetch responsibility defaults on mount
onMounted(async () => {
  await fetchResponsibilityDefaults();
});

async function fetchResponsibilityDefaults() {
  try {
    const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
    proxy.$loader.show();
    
    const response = await getResponsibilityIdV2(accountId, route.params.objId, 'responsibility');
    const data = response.data;

    const givenDays = data.when_forced_expiration_days_default ?? data.when_given_expire_in_days;
    if (givenDays != null) {
      const s = String(givenDays);
      whenGivenExpiresInDays.value = s;
      originalWhenGivenExpiresInDays.value = s;
    }
    if (data.when_awarded_expiration_days_default != null) {
      const s = String(data.when_awarded_expiration_days_default);
      whenAwardedExpiresInDays.value = s;
      originalWhenAwardedExpiresInDays.value = s;
    }
    if (data.when_awarded_expire_in_days_from_becoming_obsolete != null) {
      const s = String(data.when_awarded_expire_in_days_from_becoming_obsolete);
      whenAwardedExpiresFromObsoleteInDays.value = s;
      originalWhenAwardedExpiresFromObsoleteInDays.value = s;
    }

    if (data.when_forced_does_not_expire_default) {
      whenGivenSelected.value = 'does_not_expire';
    } else {
      whenGivenSelected.value = 'expire_in_days';
    }
    originalWhenGivenSelected.value = whenGivenSelected.value;

    if (data.when_awarded_does_not_expire_default) {
      whenAwardedSelected.value = 'does_not_expire';
    } else if (data.when_awarded === 'expire_when_obsolete' || data.expire_only_if_obsolete_default) {
      whenAwardedSelected.value = 'expire_from_obsolete';
    } else {
      whenAwardedSelected.value = 'expire_in_days';
    }
    originalWhenAwardedSelected.value = whenAwardedSelected.value;
  } catch (error) {
    console.error('Error fetching responsibility defaults:', error);
  } finally {
    proxy.$loader.hide();
  }
}
</script>

<style scoped>
.res-defaults {
  padding: 0px 12px 12px 12px;
}
.header-section {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 0;
}

.header-left {
  flex: 1;
}

.header-right {
  flex-shrink: 0;
  margin-left: 20px;
}

.res-defaults .main-heading {
  font-family: Inter;
  font-size: 18px;
  font-weight: 600;
  line-height: 28px;
  text-align: left;
  color: #101828;
}
.res-defaults .main-description {
  font-family: Inter;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  text-align: left;
  color: #475467;
}
.res-defaults .section-heading {
  font-family: Inter;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  text-align: left;
  color: #344054;
}
.res-defaults .section-description {
  font-family: Inter;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  text-align: left;
  color: #475467;
}
.hr {
  height: 1px;
  background: #EAECF0;
}
.days-input {
  margin-bottom: 12px;
}
.filter-radio {
  margin-left: -4px;
}
::v-deep .filter-radio .v-input--selection-controls__ripple {
    border-radius: 50%;
    cursor: pointer;
    height: 24px;
    position: absolute;
    transition: inherit;
    width: 24px;
    left: 0px;
    top: 1px;
    margin: 0px;
}
/* v-switch */
.custom-switch {
  margin-bottom: 12px;
  margin-left: 4px;
}
::v-deep .custom-switch.v-input--switch .v-input--switch__thumb {
  background: white !important;
  box-shadow: 0px 4px 4px 0px #00000017;
}
::v-deep .custom-switch.v-input--is-label-active.v-input--switch--inset .v-input--switch__track {
  opacity: 0.82;
}
::v-deep .custom-switch.v-input--switch--inset .v-input--switch__track {
  opacity: 0.1;
}
::v-deep .custom-switch .v-input--selection-controls__ripple {
  height: 28px;
  width: 28px;
  margin: 10px;
  opacity: 0.3;
}
::v-deep .custom-switch.v-input--is-label-active .v-input--selection-controls__ripple {
  opacity: 0.7;
}
::v-deep .custom-switch.v-input--selection-controls {
  margin-top: 0px;
}
::v-deep .custom-switch .v-input__control .v-input__slot label {
  font-family: Inter;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  text-align: right;
  color: #101828;
}
.res-defaults .label {
  font-family: Inter;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: #101828;
}
.v-input--selection-controls {
  margin-top: 0px;
}
.ghost-button {
  background-color: transparent !important;
  color: #344054 !important;
  box-shadow: none !important;
}
.ex-discard-default {
  width: 92px !important;
  position: relative !important;
  left: -10px;
}
.ex-save-default {
  width: 70px !important;
}
::v-deep.filter-radio-tab .mdi-radiobox-blank::before {
  content: url('@components/assets/uncheckedRadioIcon.svg');
}
::v-deep.filter-radio-tab .mdi-radiobox-marked::before {
  content: url('@components/assets/checkedRadioIcons.svg'); 
}
</style>
